import LoadingButton from '@app/src/components/LoadingButton'
import {
  useAssignEntity,
  useDeduplicationSummary,
  useRemoveAssignment,
  useUpdateDeduplicationDecision,
} from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import type { DeduplicationProgressUpdate, DeduplicationSummary, ImportSession } from '@app/src/types/tabularImport'
import { DeduplicationUserAction } from '@app/src/types/tabularImport'
import { CheckCircle } from '@mui/icons-material'
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'
import DeduplicationSection from './DeduplicationSection'
import type { EntitySearchAssignProps } from './EntitySearchAssignProps'

type DeduplicationReviewStepProps = {
  sessionId: string
  importStatus: ImportSession
  dedupProgress: DeduplicationProgressUpdate | null
  isImporting: boolean
  onFinalImport: () => void
  onGoBack: () => void
  assignSearch: (props: EntitySearchAssignProps) => React.ReactNode
  matchDescription: string
  entityNounPlural: string
}

type SectionColor = 'warning' | 'info' | 'success' | 'default'

type SectionConfig = {
  key: string
  title: string
  description: string
  color: SectionColor
  action: DeduplicationUserAction
  countKey: keyof Pick<DeduplicationSummary, 'needsReviewCount' | 'confirmedCount' | 'newCount' | 'skippedCount'>
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

const buildSectionConfig = (entityNounPlural: string): SectionConfig[] => [
  {
    key: 'needsReview',
    title: 'Needs Review',
    description: 'Potential matches that require your decision',
    color: 'warning',
    action: DeduplicationUserAction.Pending,
    countKey: 'needsReviewCount',
  },
  {
    key: 'confirmed',
    title: 'Confirmed Updates',
    description: `High-confidence matches that will update existing ${entityNounPlural}`,
    color: 'info',
    action: DeduplicationUserAction.Update,
    countKey: 'confirmedCount',
  },
  {
    key: 'new',
    title: `New ${capitalize(entityNounPlural)}`,
    description: 'No existing match found — will be created as new',
    color: 'success',
    action: DeduplicationUserAction.CreateNew,
    countKey: 'newCount',
  },
  {
    key: 'skipped',
    title: 'Skipped',
    description: 'Rows that will not be imported',
    color: 'default',
    action: DeduplicationUserAction.Skip,
    countKey: 'skippedCount',
  },
]

const colorForIndicator = (color: SectionColor) => (color === 'default' ? 'text.disabled' : `${color}.main`)

const DetectionInProgress: React.FC<{
  processedRows: number
  totalRows: number
  dedupProgress: DeduplicationProgressUpdate | null
  entityNounPlural: string
  onGoBack: () => void
}> = ({ processedRows, totalRows, dedupProgress, entityNounPlural, onGoBack }) => {
  const pct = totalRows > 0 ? (processedRows / totalRows) * 100 : 0

  return (
    <Stack spacing={4} alignItems="center" py={8}>
      <Typography variant="h6">Analyzing duplicates...</Typography>
      <Typography variant="body2" color="text.secondary">
        Comparing import data against existing {entityNounPlural}. You can close this and come back later.
      </Typography>
      <Box sx={{ width: '100%', maxWidth: 500 }}>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} />
        <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
          {processedRows.toLocaleString()} of {totalRows.toLocaleString()} rows processed
        </Typography>
      </Box>
      {dedupProgress && (
        <Box display="flex" gap={3}>
          <Typography variant="caption">{dedupProgress.matchedCount} matched</Typography>
          <Typography variant="caption">{dedupProgress.newCount} new</Typography>
          <Typography variant="caption">{dedupProgress.needsReviewCount} needs review</Typography>
        </Box>
      )}
      <Button onClick={onGoBack}>Go back</Button>
    </Stack>
  )
}

const SummaryCard: React.FC<{
  section: SectionConfig
  count: number
  expanded: boolean
  onClick: () => void
}> = ({ section, count, expanded, onClick }) => {
  const indicatorColor = colorForIndicator(section.color)
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: '1 1 0',
        minWidth: 200,
        p: 2,
        border: 1,
        borderColor: expanded ? indicatorColor : 'divider',
        borderRadius: 2,
        cursor: count > 0 ? 'pointer' : 'default',
        opacity: count === 0 ? 0.5 : 1,
        transition: 'border-color 0.2s',
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: indicatorColor }} />
        <Typography variant="subtitle2">{section.title}</Typography>
      </Box>
      <Typography variant="h4">{count.toLocaleString()}</Typography>
    </Box>
  )
}

const ReviewActionBar: React.FC<{
  isImporting: boolean
  disableImport: boolean
  unresolvedCount: number
  onFinalImport: () => void
  onGoBack: () => void
}> = ({ isImporting, disableImport, unresolvedCount, onFinalImport, onGoBack }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center">
    <Button onClick={onGoBack}>Go back</Button>
    <Box display="flex" gap={2} alignItems="center">
      {unresolvedCount > 0 && (
        <Typography variant="body2" color="warning.main">
          Resolve {unresolvedCount} pending review{unresolvedCount > 1 ? 's' : ''} before importing
        </Typography>
      )}
      <LoadingButton
        loading={isImporting}
        variant="contained"
        onClick={onFinalImport}
        disabled={disableImport}
        startIcon={<CheckCircle />}
      >
        Import
      </LoadingButton>
    </Box>
  </Box>
)

const DeduplicationReviewStep: React.FC<DeduplicationReviewStepProps> = ({
  sessionId,
  importStatus,
  dedupProgress,
  isImporting,
  onFinalImport,
  onGoBack,
  assignSearch,
  matchDescription,
  entityNounPlural,
}) => {
  const isDetecting = importStatus.status === 'DetectingDuplicates'
  const sectionConfig = buildSectionConfig(entityNounPlural)

  const { data: summary } = useDeduplicationSummary(sessionId, {
    refetchInterval: isDetecting ? 5000 : false,
  })

  const updateDecision = useUpdateDeduplicationDecision(sessionId)
  const assignEntity = useAssignEntity(sessionId)
  const removeAssignment = useRemoveAssignment(sessionId)

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(['needsReview']))

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (isDetecting) {
    const processedRows = dedupProgress?.processedRows ?? summary?.processedRows ?? 0
    const totalRows = dedupProgress?.totalRows ?? summary?.totalRows ?? importStatus.successfulRows

    return (
      <DetectionInProgress
        processedRows={processedRows}
        totalRows={totalRows}
        dedupProgress={dedupProgress}
        entityNounPlural={entityNounPlural}
        onGoBack={onGoBack}
      />
    )
  }

  // Block import until the summary has loaded and shows zero pending reviews.
  const unresolvedCount = summary?.needsReviewCount ?? 0
  const disableImport = summary == null || unresolvedCount > 0

  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Deduplication Review
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {importStatus.successfulRows} {entityNounPlural} analyzed. Review and confirm.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {matchDescription}
        </Typography>
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap">
        {sectionConfig.map(section => {
          const count = summary?.[section.countKey] ?? 0
          return (
            <SummaryCard
              key={section.key}
              section={section}
              count={count}
              expanded={expandedSections.has(section.key)}
              onClick={() => count > 0 && toggleSection(section.key)}
            />
          )
        })}
      </Box>

      {sectionConfig.map(section => {
        const count = summary?.[section.countKey] ?? 0
        if (count === 0) return null
        return (
          <DeduplicationSection
            key={section.key}
            sessionId={sessionId}
            title={section.title}
            description={section.description}
            color={section.color}
            count={count}
            action={section.action}
            expanded={expandedSections.has(section.key)}
            onToggle={() => toggleSection(section.key)}
            updateDecision={updateDecision}
            assignEntity={assignEntity}
            removeAssignment={removeAssignment}
            assignSearch={assignSearch}
          />
        )
      })}

      <ReviewActionBar
        isImporting={isImporting}
        disableImport={disableImport}
        unresolvedCount={unresolvedCount}
        onFinalImport={onFinalImport}
        onGoBack={onGoBack}
      />
    </Stack>
  )
}

export default DeduplicationReviewStep
